/**
 * Gốc API.
 *
 * Bản mobile được Laravel phục vụ ngay tại mvp-mobile.test nên mặc định gọi
 * cùng gốc — không cần khai tên miền, và không dính CORS. Đặt VITE_API_URL khi
 * cần trỏ sang máy chủ khác (ví dụ chạy Vite dev server riêng).
 */
export const API_URL = (import.meta.env.VITE_API_URL as string) || '';

/** Tiền tố endpoint của MVP. */
export const API_BASE = `${API_URL}/api`;

/**
 * Fetch wrapper đơn giản, tự gắn Content-Type và Authorization.
 *
 * @example
 *   const data = await apiFetch('/login', { method: 'POST', body: JSON.stringify({ email, password }) });
 */
/**
 * Đọc phản hồi, chịu được cả khi máy chủ không trả JSON.
 *
 * Khi PHP chết hay quá tải, nginx trả trang lỗi HTML kèm mã 502/504. Gọi thẳng
 * `res.json()` lúc đó ném ra "Unexpected token '<'" — người dùng đọc không hiểu
 * gì, mà thật ra chỉ là máy chủ đang không phục vụ được.
 *
 * Đọc thành chuỗi trước rồi mới thử phân tích, nên lỗi báo ra nói đúng chuyện
 * gì đang xảy ra và kèm mã HTTP để lần ra nguyên nhân.
 */
/**
 * Phiên hết hiệu lực thì báo cho AuthProvider biết để xoá token và đưa về màn
 * đăng nhập.
 *
 * Token Sanctum bị xoá khi tài khoản đó đăng nhập ở máy khác — bản máy tính
 * xoay token mỗi lần đăng nhập — nên máy trong kho có thể đang cầm một token đã
 * chết mà không hay. Để màn hình đứng im rồi ném ra "Unauthenticated." thì người
 * cầm máy tưởng mã ô sai, đi quét lại mấy lần vô ích.
 */
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(fn: (() => void) | null): void {
  unauthorizedHandler = fn;
}

async function readResponse<T>(res: Response): Promise<T> {
  const text = await res.text();

  let data: unknown;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // Không phải JSON: gần như chắc chắn là trang lỗi của web server.
    throw {
      message: res.status >= 500
        ? `Máy chủ đang không phản hồi (lỗi ${res.status}). Thử lại sau giây lát.`
        : `Máy chủ trả về dữ liệu không đọc được (mã ${res.status}).`,
      status: res.status,
    };
  }

  if (res.status === 401) {
    unauthorizedHandler?.();

    throw { message: "Phiên đăng nhập đã hết hạn. Hãy đăng nhập lại.", status: 401 };
  }

  if (!res.ok) {
    throw data ?? { message: `Yêu cầu thất bại (mã ${res.status}).`, status: res.status };
  }

  return data as T;
}

export async function apiFetch<T = unknown>(
  endpoint: string,
  options: RequestInit = {},
  token?: string
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers ?? {}),
  };

  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  return readResponse<T>(res);
}

export async function apiUpload<T = unknown>(
  endpoint: string,
  formData: FormData,
  token?: string
): Promise<T> {
  const headers: HeadersInit = {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    headers,
    body: formData,
  });

  return readResponse<T>(res);
}
